<!-- The popup that informs the user about the requirements of validation and whether they are met or not -->
<template>
	<div data-validation-popup v-if="validationShouldBeShown" :data-layout-width="layout.LayoutWidth" :data-validation-position="validationPosition">
		<span data-close-popup-icon
			v-on:click.prevent="closeValidationPopup()"
			v-on:keydown.enter.stop.prevent
			v-on:keyup.enter.stop.prevent="closeValidationPopup()"
			:aria-label="$store.getters.textResource(`rs_validation_popup_close`)"
			role="button"
			tabindex="0"
		> 
			<font-awesome-icon icon="times-circle" />
		</span>
		<div v-if="hasBackendMessage" data-correct-input="false">
			<span data-bubble>
				<font-awesome-icon icon="exclamation-circle" />
			</span>
			<span data-text aria-live="assertive">
				{{ $store.state.message[0].text }}
			</span>
		</div>
		<div
			v-else
            v-for="(rule, index) in allValidation"
            :key="`rule-${index}`"
            :data-correct-input="rule.isValid.toString()"
        >
			<span v-if="rule.isValid === true" data-bubble>
				<font-awesome-icon icon="check" />
			</span>
			<span v-else-if="rule.isValid === false" data-bubble>
				<font-awesome-icon icon="exclamation-circle" />
			</span>
			<span data-text aria-live="assertive">
				{{ validationTypeText(rule.validationType) }}
			</span>
			<span v-if="rule.value" data-bubble data-bubble-value aria-live="assertive">
				{{ rule.validationType === "requiredPoints" ? 0 : rule.value }}
			</span>
		</div>	
	</div>
</template>

<script>
export default {
	name: `validation-popup`,
	props: {
		// The validation that is automatically calculated in mixins.js
		questionValidation: {
			type: Array,
			required: true,
		},
		// Questions can add some extra validation if this is needed. This should only be used if as a last resort
		customValidation: {
			type: Array,
			default: function () {
				return [];
			}
		}
	},
	methods: {
		validationTypeText: function (type) {
			let stringName = ``;
			
			switch (type) {
				case `none`:
					stringName = `rs_question_is_required`;
					break;
				case `minValue`:
					stringName = `rs_min_answers`;
					break;
				case `maxValue`:
					stringName = `rs_max_answers`;
					break;
				case `requiredPoints`:
					stringName = `rs_should_be`;
					break;
			}

			return this.$store.getters.textResource(stringName);
		},
		closeValidationPopup() {
			this.$store.commit(`changeGlobal`, {
                variableName: `showValidationPopup`,
                newValue: false,
            });
		}
	},
	computed: {
		validationPosition: function () {
			return this.allValidation.filter(v => v.validationType === 'none').length > 0 ? null : ``;
		},
		// These are validation messages that come from the back-end
		hasBackendMessage: function () {
			return this.$store.state.message?.length > 0;
		},
		layout: function () {
			return this.$store.state.layout;
		},
		allValidation: function () {
			return Array.from(this.questionValidation).concat(this.customValidation);
		},
		// Decide on whether the validation should be shown
		validationShouldBeShown: function () {
			let aRuleIsInvalid = this.allValidation.some((r) => r.isValid === false);
			if(this.$store.state.message && this.$store.state.message.length > 0) {
				return true;
			}
			else if(this.$store.state.global.showValidationPopup === false) {
				return false;
			}		
			else {
				// Only when the next button has been clicked, or if the server has returned a validation message
				return aRuleIsInvalid && (this.$store.state.global.nextButtonClicked || (this.hasBackendMessage && this.hasBackendMessage.length > 0));
			}	
		}
	},
};
</script>

<style scoped>
@import "./../styles/variables.css";

[data-validation-popup] {
	position: absolute;
	top: 0;
	right: -280px;
	background: white;
	box-shadow: 0 5px 8px #ccc;
	border-radius: 8px;
	padding: 26px 45px 22px 30px;
	transition: 1s;
	z-index: 1;
	max-width: 400px;

	& > div {
		position: relative;

		&:not(:first-of-type) {
			margin-top: 15px;
		}

		&:not(:last-of-type) {
			margin-bottom: 15px;
		}
	}

	&::before {
		content: '';
        border-right: 15px solid #fff;
        border-top: 10px solid transparent;
        border-bottom: 10px solid transparent;
        position: absolute;
        left: -15px;
        top: 35%;
	}
}

[data-validation-position][data-validation-position] {
    right: -350px;
}

span[data-bubble] {
	min-width: 20px;
	height: 20px;
	position: absolute;
	display: flex;
	justify-content: center;
	align-items: center;
	top: 1px;
	border-radius: 20px;

	&:first-of-type {
		left: -18px;
	}

	&:last-of-type {
		right: -18px;
	}
}
span[data-close-popup-icon] {
	top: -10px;
    right: -9px;
	cursor: pointer;
    position: absolute;
    min-width: 44px;
    min-height: 44px;
    height: 26px;
    align-items: center;
    display: flex;
    justify-content: center;
	svg {
		font-size: 16px;
		color: #C80000;
	}
}

[data-text] {
	color: #000;
	font-size: 16px;
	font-weight: bold;
	transition: 0.7s;
	display: inline-block;
	width: 100%;
	margin-inline: 13px;

	&:first-letter {
		text-transform: capitalize;
	}
}

i,
[data-bubble] {
	font-weight: bold;
	color: #fff;
	font-size: 18px;
	transition: 0.7s;
}

[data-bubble][data-bubble-value] {
	font-size: 11px;
	padding: 5px;
}

[data-correct-input="false"] {
	[data-bubble] {
		background: #C80000;;
		svg {
			font-size: 20px;
			background: #FFF;
			color: #C80000;
		}
	}
}

[data-correct-input="true"] {
	[data-bubble] {
		background: #88dc3f;
		.fa-check {
			font-size: 11px;
		}
	}

}

[data-correct-input="neutral"] {
	[data-bubble] {
		background: var(--primary-color);
	}
}
@media only screen and (max-width: 1700px) {
	[data-validation-popup][data-validation-position] {
		right: 10px;
		top: 20px;
		border: 1px solid #ccc;
		&::before { 
			visibility: hidden;
		}
	}
}
@media only screen and (max-width: 1600px) {
	[data-validation-popup] {
		right: 10px;
		top: 20px;
		border: 1px solid #ccc;
		&::before { 
			visibility: hidden;
		}
	}
}
</style>